import React, { HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Menu } from '@uncinc/react-drupal-core';
import type { ImmutableRootState } from '@uncinc/react-drupal-core/types';

import { Container } from '@uncinc/uncinc-react-kitchen-sink';

import LogoJellinek from '../../../assets/images/logos/vendor/logo-jellinek.png';
import LogoVerslaving from '../../../assets/images/logos/vendor/logo-verslavingskunde-NL.svg';
import LogoDrinken from '../../../assets/images/logos/vendor/logo-allesoverdrinken.svg';
import LogoVPF from '../../../assets/images/logos/vendor/logo-vpf.svg';
import LogoArkin from '../../../assets/images/logos/vendor/logo-arkinpreventie.svg';
import LogoMentaalBalans from '../../../assets/images/logos/mentaalbalans.svg';

import { Button } from '../../../components';
import useClasses from '../../../hooks/useClasses';
import useCookie from '../../../hooks/useCookie';
import type { ImmutableMenuItem } from '..';
import {
  DOMAIN_MENTAAL_BALANS,
  DOMAIN_ZELFHULP_ALCOHOL,
  DOMAIN_ZELFHULP_GOKKEN,
  URL_ALLES_OVER_DRINKEN,
  URL_JELLINEK,
  URL_VERSLAVINGSKUNDE_NEDERLAND,
  URL_ARKIN,
} from '../../../config/constants';


import './style.scss';

interface MenuProps extends React.HTMLProps<HTMLAttributes<HTMLDivElement>> {
}

interface ItemProps {
  title: string;
  link?: string;
  onClick?: () => void;
}

const MenuFooterItem: React.FC<ItemProps> = ({ title, link, onClick }) => (
  <li>
    <Button
      className="menu-link"
      asLink
      href={link}
      onClick={onClick}
    >
      {title}
    </Button>
  </li>
);

const MenuFooter: React.FC<MenuProps> = ({
  className,
}) => {
  const { t } = useTranslation();

  const { buildClass, joinClasses } = useClasses('MenuFooter');
  const { updateConsent } = useCookie();
  const currentDomain = useSelector<ImmutableRootState<string>, string>((state) => state.getIn(['uiInternal', 'currentDomain', 'raw'], ''));

  return (
    <div className={joinClasses(className, buildClass())}>
      <Container>
        <footer className={joinClasses(buildClass('inner'), 'row')}>
          <div className="col-12">
            <div className={buildClass('wrapper')}>
              <div className={buildClass('logos')}>
                {currentDomain !== DOMAIN_MENTAAL_BALANS && (
                  <a href={URL_JELLINEK} target="_blank" rel="noreferrer">
                    <img src={LogoJellinek} alt="Logo Jellinek" />
                  </a>
                )}

                {currentDomain === DOMAIN_ZELFHULP_GOKKEN && (
                  <img className={buildClass('logos__logo')} src={LogoVPF} alt="Logo VPF" />
                )}

                {currentDomain === DOMAIN_ZELFHULP_ALCOHOL && (
                  <>
                    <a href={URL_ALLES_OVER_DRINKEN} target="_blank" rel="noreferrer">
                      <img src={LogoDrinken} alt="Logo Alles over drinken" />
                    </a>
                    <a href={URL_VERSLAVINGSKUNDE_NEDERLAND} target="_blank" rel="noreferrer">
                      <img src={LogoVerslaving} alt="Logo Verslavingskunde NL" />
                    </a>
                  </>
                )}

                {currentDomain === DOMAIN_MENTAAL_BALANS && (
                  <>
                    <a href={URL_ARKIN} target="_blank" rel="noreferrer">
                      <img className={buildClass('logos__logo')} src={LogoArkin} alt="Logo Arkin Preventie" />
                    </a>
                    <img className={buildClass('logos__logo', 'mentaal-balans')} src={LogoMentaalBalans} alt="Logo Mentaal Balans" />
                  </>
                )}
              </div>
              <Menu id="footer">
                {(items: ImmutableMenuItem[]) => (
                  <ul className={buildClass('items')}>
                    <MenuFooterItem
                      title={t('cookies.edit')}
                      onClick={updateConsent}
                    />

                    {items.map((item: ImmutableMenuItem) => {
                      const link = item.getIn(['link', 'url'], '');
                      const title = item.getIn(['link', 'title'], 'Untitled');

                      return (
                        <MenuFooterItem
                          key={`menu-item-${title}`}
                          link={link}
                          title={title}
                        />
                      );
                    })}
                  </ul>
                )}
              </Menu>
            </div>
          </div>
        </footer>
      </Container>
    </div>
  );
};

export default MenuFooter;
